import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Loader2, Crown, KeyRound, ArrowRight } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Doodle, Starburst } from "@/components/marketing/Decor";

export default function SuperAdminLoginPage() {
  const { loginSuperAdminStep1, loginSuperAdminStep2 } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();

  const onStep1 = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await loginSuperAdminStep1(email, password);
      setStep(2);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const m = typeof d === "object" ? d?.message : d;
      setErr(m || "Login failed");
    }
    finally { setBusy(false); }
  };

  const onStep2 = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await loginSuperAdminStep2(email, password, otp);
      const dest = (loc.state && loc.state.from) || "/superadmin/dashboard";
      navigate(dest, { replace: true });
    } catch (e) { setErr(e?.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="app-light mkt-root mkt-paper-noise min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <Toaster richColors position="top-center" />
      <Doodle.Crown className="hidden md:block absolute left-8 top-6 h-12 w-16 text-[var(--graffiti-yellow-deep)] -rotate-6" />
      <Starburst className="hidden md:block absolute right-12 top-12 h-9 w-9 text-black/80" />
      <Doodle.Heart className="hidden md:block absolute right-10 bottom-12 h-10 w-10 text-[var(--graffiti-yellow-deep)] rotate-12" />

      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center mb-6">
          <Link to="/" aria-label="City DOOH home" className="inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black rounded-md"><Logo size={150} light /></Link>
          <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-black">
            <Crown className="h-3.5 w-3.5" />
            Super Admin Portal
          </div>
        </div>
        <div className="mkt-sticker p-7 relative">
          <span className="mkt-tape absolute -top-4 left-6">Super Admin</span>
          {step === 1 ? (
            <>
              <h1 className="font-display uppercase text-3xl mt-3 leading-none">
                Sign <span className="mkt-highlight">in</span>
              </h1>
              <p className="text-sm text-black/70 mt-2">2-factor authentication required.</p>
              <form onSubmit={onStep1} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label className="uppercase text-[11px] font-bold tracking-wider text-black">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="sa-login-email" className="h-11 border-2 border-black bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="uppercase text-[11px] font-bold tracking-wider text-black">Password</Label>
                  <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} data-testid="sa-login-password" className="h-11 border-2 border-black bg-white" />
                </div>
                {err && <p className="text-sm font-bold text-[#8b1212] bg-[#fbd5d5] border-2 border-[#8b1212] rounded-md px-3 py-2" data-testid="sa-login-error">{err}</p>}
                <button type="submit" className="mkt-btn w-full justify-center" disabled={busy} data-testid="sa-login-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Continue <ArrowRight className="h-4 w-4" /></>)}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-display uppercase text-3xl mt-3 leading-none">
                Enter <span className="mkt-highlight">OTP</span>
              </h1>
              <p className="text-sm text-black/70 mt-2">
                We&#39;ve emailed a 6-digit code to <b>{email}</b>. It expires in 10 minutes.
              </p>
              <form onSubmit={onStep2} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label className="uppercase text-[11px] font-bold tracking-wider text-black">One-time password</Label>
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    className="text-center text-2xl tracking-[0.5em] font-mono h-12 border-2 border-black bg-white"
                    data-testid="sa-login-otp"
                  />
                </div>
                {err && <p className="text-sm font-bold text-[#8b1212] bg-[#fbd5d5] border-2 border-[#8b1212] rounded-md px-3 py-2" data-testid="sa-login-otp-error">{err}</p>}
                <button type="submit" className="mkt-btn w-full justify-center" disabled={busy || otp.length !== 6} data-testid="sa-login-otp-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><KeyRound className="h-4 w-4" /> Verify &amp; sign in</>)}
                </button>
                <button type="button" className="ag-btn-ghost w-full justify-center" onClick={() => setStep(1)}>
                  Back
                </button>
              </form>
            </>
          )}
          <div className="mt-5 text-xs text-black/70 text-center uppercase tracking-wider font-semibold">
            Admin?{" "}
            <Link to="/admin/login" className="font-bold text-black underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2">Admin sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
